import React, { useCallback, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import Tooltip from '../../common/Tooltip';
import { Editor, EditorState, convertToRaw } from 'draft-js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import { useStyleMap } from '../../../hooks'
import { getAncestorFromType, getBlockStyle } from '../../RichEditor/utils'
import { transformTextToEditorState } from '../../../utils/dataSync'
import { MENU_PARENT, antdFormError, getPrimaryDark } from '../../../constants/constants'
import { EditorProvider } from '../../../contexts/EditorContext'
import EditorWrapper from '../../common/EditorWrapper'
import ContextualMenu from '../../ContextualMenu/ContextualMenu'
import FloatyTextStyling from '../../Table/FloatyTextStyling'
import ButtonCommon from '../../common/ButtonCommon'

const EMPTY_RAW = JSON.stringify(convertToRaw(EditorState.createEmpty().getCurrentContent()))

// Une valeur prédéfinie de champ de texte est un contenu riche : `@` insère une
// variable, `/` une image, exactement comme dans une box texte du document.
// L'état est local et n'est écrit sur la box qu'au blur, sinon la
// resynchronisation depuis le store couperait la saisie en cours.
const PredefinedValueEditor = ({ box, value, index, onChange, disabled }) => {
  const styleMap = useStyleMap()
  const configuration = useStore(({ configuration }) => configuration)
  const handleUnselectBox = useStore(({ handleUnSelectBox }) => handleUnSelectBox)
  const [editorState, setEditorState] = useState(() => transformTextToEditorState(value))
  const [focused, setFocused] = useState(false)

  // Les ancêtres pointent la box hôte : la valeur est appliquée dans le contenu
  // de cette même box, une image insérée ici y restera cohérente.
  const menuAncestorIds = useMemo(() => [box.id], [box.id])
  const menuAncestorType = useMemo(
    () => getAncestorFromType(box.type, MENU_PARENT.content),
    [box.type]
  )

  const handleEditorChange = useCallback((next) => {
    if (disabled) return
    setEditorState((prev) => (typeof next === 'function' ? next(prev) : next))
  }, [disabled])

  const persist = useCallback(() => {
    if (disabled) return
    onChange(index, JSON.stringify(convertToRaw(editorState.getCurrentContent())))
  }, [disabled, index, onChange, editorState])

  // EditorProvider + EditorWrapper : le premier alimente les décorateurs (une
  // image insérée lit son éditeur dans ce contexte), le second gère les puces.
  // boxId dérivé de l'index : plusieurs éditeurs coexistent pour une même box,
  // EditorWrapper en fait un identifiant DOM qui doit rester unique.
  return (
    <EditorProvider setEditorState={handleEditorChange} editorState={editorState}>
      <EditorWrapper
        boxId={`${box.id}-predefined-${index}`}
        editorState={editorState}
        setEditorState={handleEditorChange}
      >
        <Editor
          readOnly={disabled}
          stripPastedStyles
          editorState={editorState}
          onChange={handleEditorChange}
          customStyleMap={styleMap}
          blockStyleFn={getBlockStyle}
          className='RichEditor-editor'
          style={{ overflow: 'visible' }}
          onFocus={() => { handleUnselectBox(); setFocused(true) }}
          onBlur={(e) => {
            // La barre de style vole le focus le temps d'un clic : en sortir
            // fermerait le menu avant que le style ne soit appliqué.
            if (e.relatedTarget?.className === 'text-styling-component') return
            setFocused(false)
            persist()
          }}
        />
      </EditorWrapper>
      {focused && ReactDOM.createPortal(
        <ContextualMenu
          editorState={editorState}
          setEditorState={handleEditorChange}
          menuAncestorType={menuAncestorType}
          menuAncestorIds={menuAncestorIds}
        />,
        document.getElementById('SelectionVariablePortal')
      )}
      {focused && ReactDOM.createPortal(
        <FloatyTextStyling
          editorState={editorState}
          setEditorState={handleEditorChange}
          colors={configuration.colors}
        />,
        document.getElementById('SelectionVariablePortal')
      )}
    </EditorProvider>
  )
}

const FormTextPredefinedValues = ({ box }) => {
  const { t } = useTranslation()
  const updateBox = useStore(({ updateBox }) => updateBox)
  const accentColor = useStore(({ currentDocument }) => getPrimaryDark(currentDocument?.type === 'template'))
  // Une variable peut vivre dans un masque (en-tête / pied de page) : la box
  // n'est alors pas dans `boxes`.
  const predefinedValues = useStore(({ boxes, masks }) =>
    [...boxes, ...masks.map((m) => m.boxes).flat()].find((b) => b.id === box.id)?.predefinedValues
  )

  const isDisabled = box.from_linked_section
  const values = useMemo(
    () => (predefinedValues ?? []).filter((value) => typeof value === 'string'),
    [predefinedValues]
  )

  const handleChange = useCallback((index, rawContent) => {
    updateBox(box.id, (b) => {
      const current = (b.predefinedValues ?? []).filter((value) => typeof value === 'string')
      if (current[index] === rawContent) return
      current[index] = rawContent
      b.predefinedValues = current
    })
  }, [box.id, updateBox])

  const handleAdd = useCallback(() => {
    updateBox(box.id, (b) => {
      b.predefinedValues = [
        ...(b.predefinedValues ?? []).filter((value) => typeof value === 'string'),
        EMPTY_RAW,
      ]
    })
  }, [box.id, updateBox])

  const handleDelete = useCallback((index) => {
    updateBox(box.id, (b) => {
      b.predefinedValues = (b.predefinedValues ?? [])
        .filter((value) => typeof value === 'string')
        .filter((_, i) => i !== index)
    })
  }, [box.id, updateBox])

  // flexShrink: 0 : la section est un item flex de la colonne du formulaire.
  return (
    <div style={{ marginBottom: 16, flexShrink: 0 }}>
      <div style={{ marginBottom: 8 }}>{t('predefined-values')}</div>
      {/* Liste bornée puis scrollable : sans ça une longue liste sortait de la
          carte, qui masque son débordement. */}
      <div style={{ maxHeight: 260, overflowY: 'auto', paddingRight: values.length > 2 ? 4 : 0 }}>
        {values.map((value, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div style={editorBoxStyle}>
              <PredefinedValueEditor
                box={box}
                value={value}
                index={index}
                onChange={handleChange}
                disabled={isDisabled}
              />
            </div>
            {!isDisabled && (
              <Tooltip title={t('delete')} mouseLeaveDelay="0">
                <FontAwesomeIcon
                  icon={icon({ name: 'trash-xmark', style: 'duotone' })}
                  onClick={() => handleDelete(index)}
                  style={{ cursor: 'pointer', color: antdFormError, marginTop: 8 }}
                />
              </Tooltip>
            )}
          </div>
        ))}
      </div>
      {!isDisabled && (
        <ButtonCommon type="add" accentColor={accentColor} onClick={handleAdd}>
          {t('add-predefined-value')}
        </ButtonCommon>
      )}
    </div>
  )
}

const editorBoxStyle = {
  flex: 1,
  minWidth: 0,
  border: '1px solid rgba(11, 34, 57, 0.15)',
  borderRadius: 8,
  padding: '6px 8px',
  minHeight: 34,
  backgroundColor: 'white',
}

export default FormTextPredefinedValues
